import React, { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import {
  MY_STUDY_TASKS_QUERY, STUDY_TASK_QUERY, START_STUDY_TASK_MUTATION,
  REMAKE_STUDY_TASK_MUTATION, DELETE_STUDY_TASK_MUTATION,
} from '../query/gqlQueries.js'
import languages from '../languages'
import {
  card, btn, primary, sectionLabel, field, muted, errText, fmtDue, POLL_MS, page, pageInner, Hear, useMyLanguages,
} from './SpeakingHomework'

// Practice homework, as in the iPhone app: paste what you had trouble with (a
// chat with your teacher, a corrected text) and say what went wrong; the Mac
// Studio makes a homework of exactly that — your mistakes corrected, the
// points behind them explained, and exercises to check yourself.

const schemaMissing = (e) => /Cannot query field|Unknown type/i.test(e?.message || '')

export function StudyHomeworkList({ onOpen }) {
  const { data, error, refetch, startPolling, stopPolling } = useQuery(MY_STUDY_TASKS_QUERY, { variables: {}, fetchPolicy: 'network-only', errorPolicy: 'all' })
  const [del] = useMutation(DELETE_STUDY_TASK_MUTATION)
  const [adding, setAdding] = useState(false)
  const tasks = data?.myStudyTasks || []
  const busy = tasks.some(t => t.status === 'making')
  useEffect(() => { if (busy) { startPolling(POLL_MS); return () => stopPolling() } }, [busy, startPolling, stopPolling])

  if (schemaMissing(error)) return null

  const remove = async (t) => {
    if (!window.confirm(`Delete “${t.title || 'this practice homework'}”?`)) return
    try { await del({ variables: { id: t.id } }); refetch() } catch (e) { window.alert(e.message) }
  }

  return (
    <div style={{ marginBottom: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={sectionLabel}>Practice homework</div>
        {!adding && <button style={{ ...btn, padding: '0.25rem 0.7rem', marginBottom: '0.5rem' }} onClick={() => setAdding(true)}>+ Add</button>}
      </div>
      {adding && <AddStudy onCancel={() => setAdding(false)} onAdded={() => { setAdding(false); refetch() }} />}
      {tasks.length === 0 && !adding && <div style={{ ...muted, padding: '0.2rem 0 0.4rem' }}>Paste something you had trouble with and say what went wrong — you get it corrected, explained, and exercises on it.</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {tasks.map(t => (
          <div key={t.id} style={{ ...card, display: 'flex', alignItems: 'center', gap: '0.8rem', flexWrap: 'wrap', cursor: t.status === 'ready' ? 'pointer' : 'default' }}
            onClick={() => t.status === 'ready' && onOpen(t.id)}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ color: 'var(--frost-text)', fontWeight: 600 }}>📝 {t.title || (t.status === 'making' ? 'Making your homework…' : 'Practice homework')}</div>
              <div style={muted}>
                {languages[t.lang] || t.lang}{t.teacher ? ` · from ${t.teacher}` : ''}{t.due ? ` · due ${fmtDue(t.due)}` : ''}
                {t.status === 'ready' ? ` · ${t.fixes.length} fixes · ${t.exercises.length} exercises` : ''}
              </div>
              {t.status === 'failed' && <div style={errText}>{t.error || 'It could not be made.'}</div>}
            </div>
            {t.status === 'making' && <span style={muted}>Making…</span>}
            {t.status !== 'making' && <button style={t.status === 'ready' ? primary : btn} onClick={(e) => { e.stopPropagation(); onOpen(t.id) }}>Open</button>}
            <button style={{ ...btn, color: 'var(--frost-muted)' }} title="Delete" onClick={(e) => { e.stopPropagation(); remove(t) }}>✕</button>
          </div>
        ))}
      </div>
    </div>
  )
}

function AddStudy({ onCancel, onAdded }) {
  const langs = useMyLanguages()
  const [lang, setLang] = useState('')
  const [text, setText] = useState('')
  const [note, setNote] = useState('')
  const [teacher, setTeacher] = useState('')
  const [due, setDue] = useState('')
  const [msg, setMsg] = useState('')
  const [start, { loading }] = useMutation(START_STUDY_TASK_MUTATION)
  const chosen = lang || langs[0]?.code || ''

  const add = async () => {
    setMsg('')
    if (!chosen) { setMsg('Add the language you’re learning to your profile first.'); return }
    if (!text.trim()) { setMsg('Paste what you had trouble with.'); return }
    try {
      await start({ variables: { lang: chosen, text: text.trim(), note: note.trim() || null, teacher: teacher.trim() || null, due: due || null } })
      onAdded()
    } catch (e) { setMsg(e.message) }
  }

  return (
    <div style={{ ...card, marginBottom: '0.6rem', display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
      <select style={field} value={chosen} onChange={e => setLang(e.target.value)}>
        {langs.map(l => <option key={l.code} value={l.code}>{l.name || languages[l.code] || l.code}</option>)}
      </select>
      <textarea style={{ ...field, minHeight: 120 }} placeholder="Paste what you had trouble with — a chat with your teacher, a corrected text, your answers…" value={text} onChange={e => setText(e.target.value)} />
      <textarea style={{ ...field, minHeight: 56 }} placeholder="What went wrong? (optional) — e.g. “I keep mixing up the cases after prepositions”" value={note} onChange={e => setNote(e.target.value)} />
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <input style={{ ...field, flex: 2, minWidth: 160 }} placeholder="Teacher (optional)" value={teacher} onChange={e => setTeacher(e.target.value)} />
        <label style={{ ...muted, flex: 1, minWidth: 150, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>Due
          <input type="date" style={{ ...field, colorScheme: 'inherit' }} value={due} onChange={e => setDue(e.target.value)} />
        </label>
      </div>
      {msg && <div style={errText}>{msg}</div>}
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <button style={btn} onClick={onCancel}>Cancel</button>
        <button style={primary} disabled={loading} onClick={add}>{loading ? 'Adding…' : 'Make it'}</button>
      </div>
      <div style={muted}>The Mac Studio makes it — a minute or two. You can close this meanwhile.</div>
    </div>
  )
}

export function StudyTaskView({ id, voiceSpeed, onClose }) {
  const { data, error, loading, startPolling, stopPolling } = useQuery(STUDY_TASK_QUERY, { variables: { id }, fetchPolicy: 'network-only', errorPolicy: 'all' })
  const [remake, { loading: remaking }] = useMutation(REMAKE_STUDY_TASK_MUTATION)
  const [del] = useMutation(DELETE_STUDY_TASK_MUTATION)
  const [again, setAgain] = useState(null) // the note for "Make it again", while open
  const [msg, setMsg] = useState('')
  const task = data?.studyTask
  const busy = task?.status === 'making'
  useEffect(() => { if (busy) { startPolling(POLL_MS); return () => stopPolling() } }, [busy, startPolling, stopPolling])

  const doRemake = async () => {
    setMsg('')
    try { await remake({ variables: { id, note: (again || '').trim() || null } }); setAgain(null) } catch (e) { setMsg(e.message) }
  }
  const remove = async () => {
    if (!window.confirm('Delete this practice homework?')) return
    try { await del({ variables: { id } }); onClose() } catch (e) { window.alert(e.message) }
  }

  const lang = task?.lang
  return (
    <div style={page}>
      <div style={pageInner}>
        <button style={btn} onClick={onClose}>← Homework</button>
        {loading && !task && <div style={{ ...muted, marginTop: '2rem' }}>Loading…</div>}
        {error && !task && <div style={{ ...errText, marginTop: '2rem' }}>{error.message}</div>}
        {task && (
          <>
            <div style={{ marginTop: '1.2rem' }}>
              <div style={sectionLabel}>Practice homework · {languages[lang] || lang}</div>
              <h2 style={{ margin: '0 0 0.2rem', fontSize: '1.6rem' }}>{task.title || 'Practice homework'}</h2>
              {task.meaning && <div style={{ color: 'var(--frost-muted)' }}>{task.meaning}</div>}
              <div style={{ ...muted, marginTop: '0.35rem' }}>{[task.teacher && `from ${task.teacher}`, task.due && `due ${fmtDue(task.due)}`].filter(Boolean).join(' · ')}</div>
              {task.summary && <p style={{ color: 'var(--frost-text)', lineHeight: 1.55, margin: '0.8rem 0 0' }}>{task.summary}</p>}
            </div>

            {busy && <div style={{ ...card, marginTop: '1rem' }}>Making your homework on the Mac Studio… a minute or two.</div>}
            {task.status === 'failed' && <div style={{ ...card, marginTop: '1rem', ...errText }}>{task.error || 'It could not be made.'}</div>}

            {!busy && task.fixes.length > 0 && (
              <Section title="Your mistakes, corrected">
                {task.fixes.map((f, i) => (
                  <div key={i} style={{ ...card, marginBottom: '0.5rem' }}>
                    <div style={{ color: 'var(--frost-wrong)', textDecoration: 'line-through', textDecorationColor: 'rgba(255,158,143,0.6)' }}>{f.said}</div>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.25rem' }}>
                      <span style={{ color: 'var(--frost-right)', fontWeight: 600, fontSize: '1.05rem' }}>{f.better}</span>
                      <Hear text={f.better} lang={lang} voiceSpeed={voiceSpeed} />
                    </div>
                    {f.meaning && <div style={muted}>{f.meaning}</div>}
                    {f.why && <div style={{ color: 'var(--frost-text)', fontSize: '0.88rem', marginTop: '0.35rem' }}>{f.why}</div>}
                  </div>
                ))}
              </Section>
            )}

            {!busy && task.points.length > 0 && (
              <Section title="What’s behind it">
                {task.points.map((p, i) => (
                  <div key={i} style={{ ...card, marginBottom: '0.5rem' }}>
                    <div style={{ fontWeight: 700 }}>{p.title}</div>
                    <div style={{ color: 'var(--frost-text)', lineHeight: 1.55, marginTop: '0.3rem', whiteSpace: 'pre-wrap' }}>{p.explanation}</div>
                    {p.examples.map((e, j) => (
                      <div key={j} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', marginTop: '0.45rem' }}>
                        <Hear text={e.said} lang={lang} voiceSpeed={voiceSpeed} />
                        <div>
                          <div>{e.said}</div>
                          {e.meaning && <div style={muted}>{e.meaning}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </Section>
            )}

            {!busy && task.exercises.length > 0 && <Exercises task={task} voiceSpeed={voiceSpeed} />}

            <div style={{ ...card, marginTop: '1.4rem' }}>
              {again === null ? (
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  <div style={{ flex: 1, minWidth: 180, ...muted }}>{task.note ? <>You said: “{task.note}”</> : 'Not quite what you needed?'}</div>
                  <button style={btn} disabled={busy} onClick={() => setAgain(task.note || '')}>Make it again</button>
                  <button style={{ ...btn, color: 'var(--frost-wrong)' }} onClick={remove}>Delete</button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <textarea style={{ ...field, minHeight: 60 }} placeholder="What went wrong — or what to focus on this time (optional)" value={again} onChange={e => setAgain(e.target.value)} />
                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <button style={btn} onClick={() => setAgain(null)}>Cancel</button>
                    <button style={primary} disabled={remaking} onClick={doRemake}>{remaking ? 'Starting…' : 'Make it again'}</button>
                  </div>
                </div>
              )}
              {msg && <div style={{ ...errText, marginTop: '0.4rem' }}>{msg}</div>}
            </div>

            {task.source && (
              <details style={{ marginTop: '1.2rem' }}>
                <summary style={{ ...muted, cursor: 'pointer' }}>What you pasted</summary>
                <div style={{ ...card, marginTop: '0.5rem', whiteSpace: 'pre-wrap', color: 'var(--frost-text)', fontSize: '0.9rem' }}>{task.source}</div>
              </details>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function Section({ title, children }) {
  return (
    <div style={{ marginTop: '1.4rem' }}>
      <div style={sectionLabel}>{title}</div>
      {children}
    </div>
  )
}

// Pick the word for the gap; it's checked at once, with why.
function Exercises({ task, voiceSpeed }) {
  const [picked, setPicked] = useState({}) // exercise index → option
  const done = Object.keys(picked).length
  const right = task.exercises.filter((x, i) => picked[i] !== undefined && same(picked[i], x.answer)).length
  // Options in a steady order per exercise (the answer isn't always first).
  const shuffled = useMemo(() => task.exercises.map(x => {
    const opts = Array.from(new Set([...(x.options || []), x.answer].filter(Boolean)))
    return opts.sort((a, b) => hash(a + x.sentence) - hash(b + x.sentence))
  }), [task])

  return (
    <Section title={`Exercises${done ? ` · ${right} of ${done} right` : ''}`}>
      {task.exercises.map((x, i) => {
        const choice = picked[i]
        const answered = choice !== undefined
        const ok = answered && same(choice, x.answer)
        const parts = x.sentence.split(/_{2,}/)
        const filled = answered ? x.sentence.replace(/_{2,}/, x.answer) : null
        return (
          <div key={i} style={{ ...card, marginBottom: '0.5rem' }}>
            <div style={{ fontSize: '1.08rem', lineHeight: 1.5 }}>
              {parts.map((p, k) => (
                <React.Fragment key={k}>
                  {p}
                  {k < parts.length - 1 && (
                    <span style={{ display: 'inline-block', minWidth: '3.2em', borderBottom: `2px solid ${answered ? (ok ? 'var(--frost-right)' : 'var(--frost-wrong)') : '#55555f'}`, textAlign: 'center', color: answered ? 'var(--frost-right)' : 'transparent', fontWeight: 600, padding: '0 0.2em' }}>
                      {answered ? x.answer : '·'}
                    </span>
                  )}
                </React.Fragment>
              ))}
              {answered && <span style={{ marginLeft: '0.4rem' }}><Hear text={filled} lang={task.lang} voiceSpeed={voiceSpeed} /></span>}
            </div>
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
              {shuffled[i].map(o => {
                const isAnswer = same(o, x.answer)
                const mine = answered && same(o, choice)
                const color = answered ? (isAnswer ? 'var(--frost-right)' : mine ? 'var(--frost-wrong)' : '#6b6b75') : 'var(--frost-text)'
                return (
                  <button key={o} disabled={answered} style={{ ...btn, color, borderColor: answered && (isAnswer || mine) ? color : 'var(--frost-border)', cursor: answered ? 'default' : 'pointer' }}
                    onClick={() => setPicked(p => ({ ...p, [i]: o }))}>{o}</button>
                )
              })}
            </div>
            {answered && (
              <div style={{ marginTop: '0.45rem', fontSize: '0.88rem' }}>
                <span style={{ color: ok ? 'var(--frost-right)' : 'var(--frost-wrong)', fontWeight: 600 }}>{ok ? '✓ Right. ' : '✗ Not quite. '}</span>
                {x.meaning && <span style={muted}>{x.meaning} </span>}
                {x.why && <div style={{ color: 'var(--frost-text)', marginTop: '0.25rem' }}>{x.why}</div>}
              </div>
            )}
          </div>
        )
      })}
      {done > 0 && (
        <button style={btn} onClick={() => setPicked({})}>Try them again</button>
      )}
    </Section>
  )
}

const same = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase()
const hash = (s) => { let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0; return h }
